import { Weekday } from 'generated/graphql';
import { useTranslation } from 'react-i18next';
import { DailyTargetRatedPowerInput } from './DailyTargetRatedPowerInput';
import { Dispatch } from 'react';
import { faker } from '@faker-js/faker/locale/de';

interface ScheduleTableProps {
  targetPowerElectric: number;
  dailyTargetPowerElectricMap: Map<number, number>;
  setDailyTargetPowerElectricMap: Dispatch<Map<number, number>>;
  panelArea: { w: number; h: number };
  padding: { left: number; top: number; right: number };
}

export function ScheduleTable({
  targetPowerElectric,
  dailyTargetPowerElectricMap,
  setDailyTargetPowerElectricMap,
  panelArea,
  padding,
}: Readonly<ScheduleTableProps>) {
  const [t] = useTranslation();
  const cellWidth = (panelArea.w - padding.left - padding.right - 24 - 10) / 7;
  const averageTargetPowerElectric =
    dailyTargetPowerElectricMap &&
    [...dailyTargetPowerElectricMap.values()].reduce((sum, value) => sum + value, 0) / dailyTargetPowerElectricMap.size;

  const weekdays = {
    0: Weekday.MONDAY,
    1: Weekday.TUESDAY,
    2: Weekday.WEDNESDAY,
    3: Weekday.THURSDAY,
    4: Weekday.FRIDAY,
    5: Weekday.SATURDAY,
    6: Weekday.SUNDAY,
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const inputs = e.currentTarget.closest('tr')?.querySelectorAll('input');
    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const nextIndex = index + 1;
        if (nextIndex < dailyTargetPowerElectricMap.size) {
          inputs?.[nextIndex]?.focus();
        }
        return;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prevIndex = index - 1;
        if (prevIndex >= 0) {
          inputs?.[prevIndex]?.focus();
        }
        return;
      }
      case 'Enter':
        e.currentTarget.blur();
    }
  };
  return (
    <table className="border-collapse">
      <thead>
        <tr>
          <th
            style={{ width: padding.left - 24 }}
            className="px-2 border-solid border-0 border-l border-r border-b border-t border-divider text-xs font-bold"
          >
            {t('SHARED_AVERAGE')}
          </th>
          {Object.values(weekdays).map((element) => (
            <th
              style={{ width: cellWidth - 16 * 2 }}
              key={element}
              className="border-box border-solid border-0 border-l border-r border-b border-t border-divider px-4 py-2 text-xs font-bold leading-4 overflow-hidden"
            >
              {t(`WEEKDAY_${element}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr className="bg-grey-50">
          <th className="border-solid border border-t-0 border-divider text-xs font-semibold">
            {`${averageTargetPowerElectric.toFixed()} kW`}
          </th>
          {[...dailyTargetPowerElectricMap.values()].map((tpe, index) => (
            <td
              key={`target-rated-power-${faker.string.uuid}`}
              className="border-box border-solid border-0 border-l border-r border-b border-divider text-xs font-medium text-center leading-4"
            >
              <DailyTargetRatedPowerInput
                value={tpe}
                index={index}
                handleKeyDown={handleKeyDown}
                dailyTargetPowerElectricMap={dailyTargetPowerElectricMap}
                setDailyTargetPowerElectricMap={setDailyTargetPowerElectricMap}
                cellWidth={cellWidth}
              />
            </td>
          ))}
        </tr>
        <tr className="bg-grey-100">
          <th className="border-solid border-0 border-r border-l border-b border-divider text-xs font-semibold">
            {`${((averageTargetPowerElectric * 100) / targetPowerElectric).toFixed()} %`}
          </th>
          {[...dailyTargetPowerElectricMap.values()].map((tpe, index) => (
            <td
              key={`target-rated-power-percent-${tpe}-${index}`}
              className="border-box border-solid border-0 border-l border-r border-b border-divider px-4 py-2 text-xs text-center leading-4"
            >
              {`${((tpe * 100) / targetPowerElectric).toFixed()} %`}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}
